import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";
import Cropper, { ReactCropperElement } from "react-cropper";

import Box from "@mui/material/Box";

import "cropperjs/dist/cropper.css";

// Avatars are square by definition — MUI's Avatar renders the stored image
// through object-fit: cover on a 1:1 box, so anything else gets centre-cropped
// at display time. That is the bug this component exists to prevent, hence a
// fixed ratio rather than a prop.
const AVATAR_ASPECT_RATIO = 1;

// Longest edge of the re-encoded crop. cropperjs' getCroppedCanvas() with no
// bounds hands back the selection at its *natural* resolution, so a 12MP phone
// headshot returns a multi-MB file and trips PhotoPicker's size gate after the
// user has already cropped something that was fine before. 512 is comfortably
// above the largest avatar we render (134px, 268px at 2x) and matches the
// webcam branch's videoConstraints.
export const CROP_OUTPUT_SIZE = 512;

// Auto-apply re-encodes a canvas, so coalesce bursts of wheel-zoom events.
const AUTO_APPLY_DEBOUNCE_MS = 200;

export interface AvatarCropHandle {
  /**
   * Rebuilds the cropped File from the current selection. Hosts submitting the
   * form should call this rather than trusting the last auto-applied value — it
   * guarantees the final on-screen selection is what gets uploaded.
   */
  applyCrop: () => File | null;
}

interface AvatarCropProps {
  imageSrc: File;
  onCropped: (file: File) => void;
  height?: number;
}

function dataUrlToFile(dataUrl: string, name: string, mimeType: string): File {
  const binaryString = atob(dataUrl.split(",")[1]);

  const binaryArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    binaryArray[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([binaryArray], { type: mimeType });
  return new File([blob], name, { type: mimeType });
}

const AvatarCrop = forwardRef<AvatarCropHandle, AvatarCropProps>(
  function AvatarCrop({ imageSrc, onCropped, height = 260 }, ref) {
    const cropperRef = useRef<ReactCropperElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Memoised and revoked rather than minted inline in `src`, which would
    // create a new blob URL on every render and leak all of them.
    const objectUrl = useMemo(() => URL.createObjectURL(imageSrc), [imageSrc]);
    useEffect(() => () => URL.revokeObjectURL(objectUrl), [objectUrl]);

    const buildCroppedFile = useCallback((): File | null => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return null;

      const cropData = cropper.getData(true);
      let options;

      if (cropData?.width) {
        // Never upscale — a source smaller than the cap keeps its own size.
        const size = Math.min(CROP_OUTPUT_SIZE, Math.round(cropData.width));
        options = { width: size, height: size };
      }

      const canvas = cropper.getCroppedCanvas(options);
      if (!canvas) return null;

      const mimeType =
        imageSrc.type === "image/jpg"
          ? "image/jpeg"
          : imageSrc.type || "image/png";

      // Encoded once: auto-apply runs this on every adjustment, so testing the
      // canvas with a second toDataURL() would double the work per drag.
      const dataUrl = canvas.toDataURL(mimeType);
      if (!dataUrl) return null;

      return dataUrlToFile(dataUrl, imageSrc.name, mimeType);
    }, [imageSrc]);

    const applyCrop = useCallback((): File | null => {
      const file = buildCroppedFile();
      if (!file) return null;

      onCropped(file);
      return file;
    }, [buildCroppedFile, onCropped]);

    // react-cropper wires ready/cropend/zoom to the cropperjs instance at init
    // and does not rebind them when the props change, so the handlers below
    // reach the current applyCrop through a ref instead of closing over it.
    const applyCropRef = useRef(applyCrop);
    applyCropRef.current = applyCrop;

    useImperativeHandle(
      ref,
      () => ({ applyCrop: () => applyCropRef.current() }),
      []
    );

    const scheduleAutoApply = useCallback(() => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => applyCropRef.current(),
        AUTO_APPLY_DEBOUNCE_MS
      );
    }, []);

    // Keyed on the source, not just unmount: a drag followed within the
    // debounce window by a paste/webcam swap would otherwise fire against the
    // replaced image. `ready` re-crops the new one anyway.
    useEffect(() => () => clearTimeout(debounceRef.current), [imageSrc]);

    const handleReady = useCallback(() => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;

      const { naturalWidth, naturalHeight } = cropper.getImageData();

      if (naturalWidth && naturalHeight) {
        // Largest square that fits, centred horizontally and anchored to the
        // TOP. cropperjs centres its default box instead, which on a portrait
        // headshot reproduces the very crop we are fixing — the forehead falls
        // outside the box before the user has touched anything.
        let width = naturalWidth;
        let cropHeight = width / AVATAR_ASPECT_RATIO;

        if (cropHeight > naturalHeight) {
          cropHeight = naturalHeight;
          width = cropHeight * AVATAR_ASPECT_RATIO;
        }

        cropper.setData({
          x: (naturalWidth - width) / 2,
          y: 0,
          width,
          height: cropHeight
        });
      }

      applyCropRef.current();
    }, []);

    return (
      <Box
        sx={{
          "& .cropper-view-box, & .cropper-face": { borderRadius: "50%" }
        }}
      >
        <Cropper
          src={objectUrl}
          style={{ height, width: "100%" }}
          aspectRatio={AVATAR_ASPECT_RATIO}
          viewMode={1}
          dragMode="move"
          autoCropArea={1}
          guides={false}
          ref={cropperRef}
          background={false}
          ready={handleReady}
          cropend={scheduleAutoApply}
          zoom={scheduleAutoApply}
        />
      </Box>
    );
  }
);

export default AvatarCrop;
